import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { UpdateBusinessCertificateDialog } from '@/features/organization-settings/components/update-business-certificate-dialog'
import { OrganizationAddressSectionCard } from '@/features/organization-settings/components/organization-address-section-card'
import { OrganizationCompanyPageHeader } from '@/features/organization-settings/components/organization-company-page-header'
import { OrganizationCompanySaveActions } from '@/features/organization-settings/components/organization-company-save-actions'
import { OrganizationContactSectionCard } from '@/features/organization-settings/components/organization-contact-section-card'
import { OrganizationInfoReadonlyCard } from '@/features/organization-settings/components/organization-info-readonly-card'
import {
  organizationAddressSchema,
  organizationContactSchema,
  organizationLocalAddressSchema,
} from '@/features/organization-settings/organization-settings.schema'
import {
  buildInitialFormState,
  LOCAL_INPUT_TO_STATE,
} from '@/features/organization-settings/organization-company-form-state'
import { useSession } from '@/shared/hooks/useSession'
import {
  useDoesOrganizationRequiresLocalAddress,
  useUpdateOrganization,
} from '@/shared/hooks/api/organizations'
import { useOrganizationDocuments } from '@/shared/hooks/use-organization-documents'
import { getOrganizationSaveDisabledReason } from '@/features/organization-settings/organization-save-disabled-reason'

export function OrganizationCompanyPage() {
  const { t } = useTranslation(['settings', 'common'])
  const { t: tCountryName } = useTranslation('countriesByIsoAlpha2')
  const { session } = useSession()
  const { organization } = session
  const { mutateAsync: updateOrganization, isPending: isSaving } =
    useUpdateOrganization(organization.id)
  const { downloadDocument } = useOrganizationDocuments({
    organizationId: organization.id,
  })

  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false)
  const [state, setState] = useState(() => buildInitialFormState(organization))
  const [isFormPristine, setIsFormPristine] = useState(true)

  const doesOrganizationRequiresLocalAddress =
    useDoesOrganizationRequiresLocalAddress(organization.placeOfRegistration)

  const latinErrorMessage = t('common:errors.latinCharacters')

  const onSingleCountryAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => ({
        ...prev,
        [id]: value,
        localStreet: id === 'street' ? value : prev.localStreet,
        localStreetNumber: id === 'streetNumber' ? value : prev.localStreetNumber,
        localSubDivision: id === 'subDivision' ? value : prev.localSubDivision,
        localPoBox: id === 'poBox' ? value : prev.localPoBox,
        localCity: id === 'city' ? value : prev.localCity,
        localPostCode: id === 'postCode' ? value : prev.localPostCode,
        localStreet2: id === 'street2' ? value : prev.localStreet2,
      }))
      setIsFormPristine(false)
    },
    []
  )

  const onCnEnglishAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => ({ ...prev, [id]: value }))
      setIsFormPristine(false)
    },
    []
  )

  const onCnLocalAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      const baseId = id.startsWith('local-') ? id.replace(/^local-/, '') : id
      const key = LOCAL_INPUT_TO_STATE[baseId]
      if (!key) {
        return
      }
      setState((prev) => ({ ...prev, [key]: value }))
      setIsFormPristine(false)
    },
    []
  )

  const onContactChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => ({ ...prev, [id]: value }))
      setIsFormPristine(false)
    },
    []
  )

  const onSaveChanges = useCallback(async () => {
    try {
      await updateOrganization({
        street: state.street,
        streetNumber: state.streetNumber,
        country: state.country,
        poBox: state.poBox,
        city: state.city,
        subDivision: state.subDivision,
        street2: state.street2,
        postCode: state.postCode,
        localStreet: state.localStreet,
        localStreetNumber: state.localStreetNumber,
        localSubDivision: state.localSubDivision,
        localPostCode: state.localPostCode,
        localPoBox: state.localPoBox,
        localCity: state.localCity,
        localStreet2: state.localStreet2,
        contactName: state.contactName,
        contactEmail: state.contactEmail,
        contactPhoneNumber: state.contactPhoneNumber,
      })
      toast.success(t('settings:organizationDetails.notifications.saveChanges'))
      setIsFormPristine(true)
    } catch {
      toast.error(t('settings:organizationDetails.notifications.saveChangesError'))
    }
  }, [state, updateOrganization, t])

  const onClickCertificate = useCallback(async () => {
    if (!organization.certificateFileName) {
      return
    }
    const result = await downloadDocument(organization.certificateFileName)
    if (!result.success) {
      toast.error(
        result.errorMessage ??
          t('common:errors.downloadFile.generic', { ns: 'common' })
      )
      return
    }
    toast.success(t('common:notifications.downloadSuccess', { ns: 'common' }))
  }, [downloadDocument, organization.certificateFileName, t])

  const isAddressUpdateEnabled = useMemo(() => {
    if (doesOrganizationRequiresLocalAddress) {
      const localOk = organizationLocalAddressSchema.safeParse({
        localStreet: state.localStreet,
        localStreetNumber: state.localStreetNumber,
        country: state.country,
        localPoBox: state.localPoBox,
        localCity: state.localCity,
        localSubDivision: state.localSubDivision,
        localStreet2: state.localStreet2,
        localPostCode: state.localPostCode,
      }).success
      const addressOk = organizationAddressSchema.safeParse(state).success
      return localOk && addressOk
    }
    return organizationAddressSchema.safeParse(state).success
  }, [state, doesOrganizationRequiresLocalAddress])

  const isContactUpdateEnabled = useMemo(() => {
    return organizationContactSchema.safeParse(state).success
  }, [state])

  const isSaveDisabled =
    !isAddressUpdateEnabled || !isContactUpdateEnabled || isFormPristine

  const saveDisabledReason = getOrganizationSaveDisabledReason({
    isFormPristine,
    isAddressUpdateEnabled,
    isContactUpdateEnabled,
  })

  const saveDisabledHint =
    saveDisabledReason === 'pristine'
      ? undefined
      : t(`settings:organizationDetails.saveDisabledHint.${saveDisabledReason}`)

  const showContactCompleteHint =
    !isFormPristine && isAddressUpdateEnabled && !isContactUpdateEnabled

  const countryCodeLower = state.country.trim().toLowerCase()
  const countryName =
    countryCodeLower.length === 2 ? tCountryName(countryCodeLower) : state.country

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
        <OrganizationCompanyPageHeader
          title={t('settings:organizationDetails.title')}
          description={t('settings:organizationDetails.pageDescription')}
          updateCertificateLabel={t(
            'settings:organizationDetails.organizationInfo.actions.updateBusinessCertificate'
          )}
          onOpenCertificate={() => setCertificateDialogOpen(true)}
        />

        <div className="space-y-6">
          <Alert>
            <Info className="size-4" />
            <AlertDescription>
              {t('settings:organizationDetails.alert')}
            </AlertDescription>
          </Alert>

          <OrganizationInfoReadonlyCard
            organization={organization}
            doesOrganizationRequiresLocalAddress={
              doesOrganizationRequiresLocalAddress
            }
            onCertificateClick={onClickCertificate}
          />

          <OrganizationAddressSectionCard
            state={state}
            countryName={countryName}
            doesOrganizationRequiresLocalAddress={
              doesOrganizationRequiresLocalAddress
            }
            latinErrorMessage={latinErrorMessage}
            onSingleCountryAddressInput={onSingleCountryAddressInput}
            onCnEnglishAddressInput={onCnEnglishAddressInput}
            onCnLocalAddressInput={onCnLocalAddressInput}
          />

          <OrganizationContactSectionCard
            contactName={state.contactName}
            contactEmail={state.contactEmail}
            contactPhoneNumber={state.contactPhoneNumber}
            showCompleteToSaveHint={showContactCompleteHint}
            onContactChange={onContactChange}
          />

          <OrganizationCompanySaveActions
            saveLabel={t('common:actions.save', { ns: 'common' })}
            isSaving={isSaving}
            isSaveDisabled={isSaveDisabled}
            saveDisabledHint={saveDisabledHint}
            onSave={onSaveChanges}
          />
        </div>
      </div>

      <UpdateBusinessCertificateDialog
        open={certificateDialogOpen}
        onOpenChange={setCertificateDialogOpen}
        organizationId={organization.id}
        placeOfRegistration={organization.placeOfRegistration}
        countryForLabel={organization.country}
        initialBusinessRegistrationNumber={
          organization.businessRegistrationNumber ?? ''
        }
        initialCertificateFileName={organization.certificateFileName ?? ''}
      />
    </>
  )
}
